import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { formatNumber } from '../../i18n/formatters';

/**
 * Shows after a successful printable purchase with two primary actions:
 * "Show in Library" navigates to the print library and clears the modal;
 * "Print Now" triggers the existing print handoff path.
 * Esc / backdrop click = close (user can do either action later).
 */
export default function PurchaseSuccessModal({
  printable,
  alreadyOwned,
  newBalance,
  onShowInLibrary,
  onPrintNow,
  onClose,
}) {
  const { t } = useTranslation('printables');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!printable) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10100,
        background: 'rgba(27,37,80,0.31)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: 'inherit',
      }}
    >
      <div style={{
        background: '#FFFFFF', borderRadius: 20,
        border: '1px solid rgba(27,37,80,0.076)',
        width: 440, maxWidth: '95vw', position: 'relative',
        padding: 28,
      }}>
        <button
          onClick={onClose}
          aria-label={t('common:actions.close')}
          style={{
            position: 'absolute', top: 14, right: 14,
            width: 30, height: 30, borderRadius: '50%', border: 'none',
            background: '#FFFFFF', color: '#1B2550',
            fontSize: '1rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ✕
        </button>

        {/* Checkmark + headline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'rgba(38,199,116,0.2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M5 12l5 5L20 7" stroke="rgba(38,199,116,0.95)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1B2550' }}>
            {alreadyOwned ? t('success.alreadyOwned') : t('success.added')}
          </h2>
        </div>

        {/* Thumbnail */}
        <div style={{
          width: '100%', aspectRatio: '2/3', maxHeight: 280,
          borderRadius: 12, overflow: 'hidden',
          background: '#EEF0F6', marginBottom: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {printable.thumbnailUrl || printable.previewUrl ? (
            <img
              src={printable.previewUrl || printable.thumbnailUrl}
              alt={printable.title}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <div style={{ color: 'rgba(27,37,80,0.19)', fontSize: '0.85rem' }}>{t('success.noPreview')}</div>
          )}
        </div>

        {/* Title */}
        <div style={{
          fontSize: '0.95rem', fontWeight: 600, color: '#1B2550',
          lineHeight: 1.35, marginBottom: 6,
        }}>
          {printable.title}
        </div>

        {/* Balance */}
        {typeof newBalance === 'number' && (
          <div style={{ fontSize: '0.78rem', color: 'rgba(27,37,80,0.427)', marginBottom: 20 }}>
            <Trans
              t={t}
              i18nKey="success.creditsRemaining"
              values={{ balance: formatNumber(newBalance) }}
              components={{ bold: <strong style={{ color: 'rgba(27,37,80,0.807)' }} /> }}
            />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onShowInLibrary}
            style={{
              flex: 1, padding: '12px 14px', borderRadius: 10, border: 'none',
              background: '#FFFFFF', color: 'rgba(27,37,80,0.855)',
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,37,80,0.133)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(27,37,80,0.076)'}
          >
            {t('success.showInLibrary')}
          </button>
          <button
            onClick={onPrintNow}
            style={{
              flex: 1, padding: '12px 14px', borderRadius: 10, border: 'none',
              background: '#26C774', color: '#FFFFFF',
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#189A57'}
            onMouseLeave={e => e.currentTarget.style.background = '#26C774'}
          >
            {t('success.printNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
